import React from 'react';
import { Box } from '@mui/material';
import ProductSelectionSendTab from '../../ProductSelectionSendTab';
import DealerSendHistoryTab from '../../DealerSendHistoryTab';
import DetailPanelSelectionsTab from './DetailPanelSelectionsTab';
import DetailPanelAppSheetTab from '../appsheet/DetailPanelAppSheetTab';
import DetailPanelTradeTab from '../appsheet/DetailPanelTradeTab';
import DetailPanelJsonView from './DetailPanelJsonView';
import {
  DETAIL_PANEL_TAB_SELECTIONS,
  DETAIL_PANEL_TAB_RAW_JSON,
  DETAIL_PANEL_TAB_EDIT_AND_SEND,
  DETAIL_PANEL_TAB_HISTORY,
  DETAIL_PANEL_TAB_APPSHEET,
  DETAIL_PANEL_TAB_TRADE,
} from '../constants/detailPanelTabs';

// DetailPanelContent is the tab-to-component switchboard for the dealer drawer.
// Keeping the mapping centralized makes the detail workflow easy to audit by tab.
const DetailPanelContent = ({
  activeTab,
  currentRow,
  dealerContext,
  userEmail,
  programLabel,
  programCode,
  programKey,
  isHydratingDetails,
  isRefreshing,
  jsonCollapsed,
  setJsonCollapsed,
  rawSelections,
  productSelections,
  selectedSelectionIndex,
  setSelectedSelectionIndex,
  setActiveTab,
  notifyFromSendTab,
  handleDetailPaneSelectionRowClick,
  handleRefreshProgram,
  onNavigateToDealer,
  enrollmentStatusMeta,
  apiPresenceStatusMeta,
  productStatusMeta,
  terminationStatusMeta,
  liveSelectionsMeta,
  programConsentMeta,
  dataConsentMeta,
  buildStatusChip,
  formatDate,
}) => {
  // Selections is the default operational tab and owns refresh/status review for
  // the currently selected enrollment row.
  if (activeTab === DETAIL_PANEL_TAB_SELECTIONS) {
    return (
      <DetailPanelSelectionsTab
        currentRow={currentRow}
        programLabel={programLabel}
        programCode={programCode}
        programKey={programKey}
        isHydratingDetails={isHydratingDetails}
        isRefreshing={isRefreshing}
        refreshDisabled={isRefreshing}
        onRefresh={handleRefreshProgram}
        enrollmentStatusMeta={enrollmentStatusMeta}
        apiPresenceStatusMeta={apiPresenceStatusMeta}
        productStatusMeta={productStatusMeta}
        terminationStatusMeta={terminationStatusMeta}
        liveSelectionsMeta={liveSelectionsMeta}
        programConsentMeta={programConsentMeta}
        dataConsentMeta={dataConsentMeta}
        buildStatusChip={buildStatusChip}
        formatDate={formatDate}
        productSelections={productSelections}
        userEmail={userEmail}
        onNavigateToDealer={onNavigateToDealer}
        onRowClick={handleDetailPaneSelectionRowClick}
        onNotify={notifyFromSendTab}
      />
    );
  }

  if (activeTab === DETAIL_PANEL_TAB_APPSHEET) {
    return <DetailPanelAppSheetTab bac={dealerContext.bac} userEmail={userEmail} programName={programKey} onNotify={notifyFromSendTab} />;
  }

  if (activeTab === DETAIL_PANEL_TAB_TRADE) {
    return <DetailPanelTradeTab bac={dealerContext.bac} userEmail={userEmail} programName="Trade" onNotify={notifyFromSendTab} />;
  }

  if (activeTab === DETAIL_PANEL_TAB_RAW_JSON) {
    // Raw JSON exposes the source payload so support and audit flows can compare
    // rendered UI state against the underlying enrollment object.
    return (
      <Box
        sx={{
          flex: 1,
          p: 2,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          minHeight: 0,
        }}
      >
        <DetailPanelJsonView
          src={currentRow._original || currentRow}
          collapsed={jsonCollapsed}
          onCollapseAll={() => setJsonCollapsed(true)}
          onExpandAll={() => setJsonCollapsed(false)}
        />
      </Box>
    );
  }

  if (activeTab === DETAIL_PANEL_TAB_EDIT_AND_SEND) {
    // Edit-and-send hands off to the VMT workflow while preserving the selection
    // context the user established in the detail drawer.
    return (
      <ProductSelectionSendTab
        selections={rawSelections}
        dealerContext={dealerContext}
        initialSelectionIndex={selectedSelectionIndex}
        onSelectionChange={setSelectedSelectionIndex}
        onNotify={notifyFromSendTab}
        onRequestTabChange={setActiveTab}
      />
    );
  }

  if (activeTab === DETAIL_PANEL_TAB_HISTORY) {
    // History is intentionally read-only so send auditing stays separate from the
    // mutation surface used to create new outbound payloads.
    return <DealerSendHistoryTab bac={dealerContext.bac} />;
  }

  return null;
};

export default DetailPanelContent;
